import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Primereact components
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';

// Column
import { FreshApplicationColumns, ViewAllBookAppointmentColumns } from '../../../appconfig/DatatableSetting';

// Data table
import { defaultRowOptions, defaultPageRowOptions, Skeletonitems, SkeletonbodyTemplate, paginatorLinks, showingEntries } from '../../../appconfig/Settings';

//Services
import { getAllDetails } from '../../../service/ProductService';

import AppBreadcrumb from '../../../components/CustomeBreadcrumb/AppBreadcrumb';
import moment from 'moment';
import { BOOKED_APPOINTMENTLIST } from '../../../config/ApiConstant';
import { formatDateTime, setAppStatus } from '../../../appconfig/AppHelper';
import CustomDatePicker from '../../../components/CustomDatePicker/CustomDatePicker';
import { commonNameTemplate, dateTemplate } from '../../../appconfig/DatatableTemplateSetting';
import { NavLink } from 'react-router-dom';

export const ViewAllBookedAppointment = () => {
    document.title = 'View All Booked Appointments | GSPC';

    //Set Toast/ Filter Properties
    const toast = useRef<any>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<any>(null);

    // Navigate
    const navigate = useNavigate();

    // Date Object
    const storedDates = localStorage.getItem('booked_appointment_app');
    let initialStartDate = moment().subtract(15, 'days').toDate();
    let initialEndDate = moment().toDate();

    if (storedDates) {
        try {
            const parsed = JSON.parse(storedDates);
            initialStartDate = parsed.startDate ? new Date(parsed.startDate) : initialStartDate;
            initialEndDate = parsed.endDate ? new Date(parsed.endDate) : initialEndDate;
        } catch (error) {
            console.error('Error parsing stored dates', error);
        }
    }

    const [dates, setDates] = useState({
        start: initialStartDate,
        end: initialEndDate
    });

    //Loading/Page Loading
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [appointmentsList, setAppointmentsList] = useState<any>([]);

    // useEffect
    useEffect(() => {
        getAllBookedAppointmentsList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [dates]);

    // On Date Change
    const onDateChange = (start: any, end: any) => {
        setDates({ start, end });

        if (start && end) {
            localStorage.setItem(
                'booked_appointment_app',
                JSON.stringify({
                    startDate: moment(start).format('YYYY-MM-DD'),
                    endDate: moment(end).format('YYYY-MM-DD')
                })
            );
        }
    };

    // Get all booked appointments list
    const getAllBookedAppointmentsList = async () => {
        try {
            setPageLoad(false);
            let startDate = moment(dates?.start).format('y-M-D') + ' 00:00:00';
            let endDate = moment(dates?.end).format('y-M-D') + ' 23:59:59';
            const response = await getAllDetails(BOOKED_APPOINTMENTLIST + '?start_date=' + startDate + '&end_date=' + endDate);
            if (response) {
                const DataList: any = response;
                if (DataList.length === 0) {
                    setAppointmentsList([]);
                } else {
                    setAppointmentsList(DataList);
                }
                setPageLoad(true);
            } else {
                setPageLoad(false);
                setAppointmentsList([]);
            }
        } catch (error: any) {
            if (error.response) {
                setPageLoad(false);
                setAppointmentsList([]);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Sorry Could Not Load Data'
                });
            }
        }
    };

    // Right toolbar
    const rightToolbarTemplate = () => {
        return (
            <>
                <CustomDatePicker startDate={dates?.start} endDate={dates?.end} onChange={onDateChange} />
            </>
        );
    };

    // Template for actions
    const actionsTemplate = (rowData: any) => {
        return (
            <>
                <div className="flex">
                    <Button label="View" onClick={() => navigate(`/app/application/applicationViewForm/${btoa(rowData?.SrNo)}`)} />
                </div>
            </>
        );
    };

    // Template for status
    const statusTemplate = (rowData: any) => {
        let appStatusVal = setAppStatus(rowData?.appStatus);
        return (
            <>
                <Badge severity={appStatusVal.severity} value={appStatusVal.value}></Badge>
            </>
        );
    };

    // Template for name
    const nameTemplate = (rowData: any) => {
        return (
            <>
                <NavLink to={`/app/application/applicationViewForm/${btoa(rowData.SrNo)}`}>
                    <div className="avatar-div flex align-items-center">
                        <div className="emp-name link-name ml-2">{rowData?.name}</div>
                    </div>
                </NavLink>
            </>
        );
    };

    // Template for appointment date
    const appointmentDateTemplate = (rowData: any) => {
        return <>{formatDateTime(rowData?.appointment_date)}</>;
    };

    return (
        <>
            <Toast ref={toast} />
            <AppBreadcrumb currentMenu={'Applications'} pageName={'Booked Appointment List'} pageTitle={'Booked Appointment List'} goToPage={true} />
            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toolbar right={rightToolbarTemplate}></Toolbar>

                        {/* Datatable */}
                        {pageLoad ? (
                            <>
                                <DataTable
                                    value={appointmentsList}
                                    showGridlines
                                    stripedRows
                                    rows={defaultRowOptions}
                                    paginator={appointmentsList.length >= 10 && true}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    globalFilter={globalFilter}
                                    filters={globalFieldFilter}
                                    onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                    className="datatable-responsive p-datatable-sm"
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                    emptyMessage="No Appointments Found"
                                >
                                    {ViewAllBookAppointmentColumns.map((col, i) => {
                                        if (col.field === 'action') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={actionsTemplate} />;
                                        } else if (col.field === 'appointment_date') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={appointmentDateTemplate} sortable />;
                                        } else if (col.field === 'BOD') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={dateTemplate} />;
                                        } else if (col.field === 'appStatus') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={statusTemplate} style={{ minWidth: '13rem' }} />;
                                        } else if (col.field === 'name') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={nameTemplate} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'SrNo') {
                                            return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'BillSrNo') {
                                            return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'PayDt') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={dateTemplate} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'appointment_date') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={appointmentDateTemplate} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'PPhone') {
                                            return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'DegNm') {
                                            return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'ColName') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={commonNameTemplate} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'sr_no') {
                                            return <Column key={col.field} header={col.header} body={(_, { rowIndex }) => rowIndex + 1} />;
                                        } else {
                                            return <Column key={col.field} field={col.field} header={col.header} filter filterPlaceholder={'Search by ' + col.header} />;
                                        }
                                    })}
                                </DataTable>
                            </>
                        ) : (
                            <>
                                {/* Skeleton Data table */}
                                <DataTable value={Skeletonitems}>
                                    {ViewAllBookAppointmentColumns.map((col, i) => (
                                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                    ))}
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
